import React, { useState, useEffect, useCallback } from 'react';
import { widgetFetchers } from '../../../services/widgetDataService';
import { getWidgetDefinition } from './widgetRegistry';
import { useAuth } from '../../../contexts/AuthContext';
import KPIWidget from './KPIWidget';
import ChartWidget from './ChartWidget';
import ListWidget from './ListWidget';
import MiniCalendarWidget from './MiniCalendarWidget';
import AtRiskWidget from './AtRiskWidget';

interface WidgetWrapperProps {
  widgetType: string;
  config: Record<string, any>;
  isEditMode: boolean;
  onRemove: () => void;
  width: number;
  height: number;
}

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  widgetType, config, isEditMode, onRemove, width, height,
}) => {
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const definition = getWidgetDefinition(widgetType);

  const fetchData = useCallback(async () => {
    const fetcher = widgetFetchers[widgetType];
    if (!fetcher) { setError('Widget desconhecido'); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher(config, user?.id);
      setData(result);
    } catch {
      setError('Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [widgetType, config, user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const renderWidget = () => {
    const props = { data, config, loading, error, width, height };
    const category = definition?.category;

    if (category === 'kpi') return <KPIWidget {...props} widgetType={widgetType} />;
    if (category === 'chart') return <ChartWidget {...props} widgetType={widgetType} />;
    if (category === 'calendar') return <MiniCalendarWidget {...props} />;
    if (widgetType === 'list_at_risk_opportunities') return <AtRiskWidget {...props} />;
    if (category === 'list') return <ListWidget {...props} widgetType={widgetType} />;
    return <div style={{ padding: 'var(--space-4)', color: 'var(--text-tertiary)' }}>Widget nao encontrado</div>;
  };

  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: 'var(--glass-bg-strong)',
      backdropFilter: 'var(--glass-blur)',
      WebkitBackdropFilter: 'var(--glass-blur)',
      borderRadius: 'var(--radius-xl)',
      border: '1px solid var(--glass-border)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      {/* Edit mode controls */}
      {isEditMode && (
        <>
          {/* Drag handle */}
          <div
            className="widget-drag-handle"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              zIndex: 10,
              background: 'rgba(99, 102, 241, 0.08)',
              borderBottom: '1px solid var(--glass-border)',
            }}
          >
            <svg width="16" height="6" viewBox="0 0 16 6" fill="none">
              <circle cx="2" cy="1" r="1" fill="var(--text-tertiary)" />
              <circle cx="8" cy="1" r="1" fill="var(--text-tertiary)" />
              <circle cx="14" cy="1" r="1" fill="var(--text-tertiary)" />
              <circle cx="2" cy="5" r="1" fill="var(--text-tertiary)" />
              <circle cx="8" cy="5" r="1" fill="var(--text-tertiary)" />
              <circle cx="14" cy="5" r="1" fill="var(--text-tertiary)" />
            </svg>
          </div>
          {/* Remove button */}
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(239, 68, 68, 0.8)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 11,
              fontSize: '12px',
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        </>
      )}

      {/* Widget content */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        paddingTop: isEditMode ? '28px' : 0,
      }}>
        {renderWidget()}
      </div>
    </div>
  );
};

export default WidgetWrapper;
